/**
 * Chamada as tools pela API publica, que e a mesma que o MCP do Igor usa.
 *
 * Leitura e GET com a entrada na query string; escrita e POST com JSON. A
 * rota sai do nome da tool (`leads.registrar` -> /api/v1/leads/registrar),
 * entao a tela nunca monta caminho a mao.
 */
export type FalhaApi = { codigo: string; mensagem: string; campo?: string };

export class ErroApi extends Error {
  readonly status: number;
  readonly codigo: string;
  readonly falhas: readonly FalhaApi[];

  constructor(status: number, codigo: string, mensagem: string, falhas: readonly FalhaApi[]) {
    super(mensagem);
    this.status = status;
    this.codigo = codigo;
    this.falhas = falhas;
  }

  /** A mensagem do campo, para mostrar embaixo dele no formulario. */
  doCampo(campo: string): string | undefined {
    return this.falhas.find((f) => f.campo === campo)?.mensagem;
  }
}

type Corpo = { code?: string; message?: string; details?: unknown };

function falhasDe(corpo: Corpo): FalhaApi[] {
  const d = corpo.details;
  if (!Array.isArray(d)) return [];
  // a API publica devolve { campo, erro } por falha (rotas/api-publica.ts)
  return d.map((x: { campo?: string | null; erro?: string; codigo?: string }) => ({
    codigo: x.codigo ?? 'erro',
    mensagem: x.erro ?? '',
    ...(x.campo != null && x.campo !== '' ? { campo: x.campo } : {}),
  }));
}

function rotaDa(tool: string): string {
  const [modulo, acao] = tool.split('.');
  return `/api/v1/${modulo}/${acao}`;
}

function querystring(entrada: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(entrada)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s === '' ? '' : `?${s}`;
}

async function enviar<T>(url: string, init: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: 'same-origin', ...init });
  const texto = await r.text();
  const corpo = (texto === '' ? {} : JSON.parse(texto)) as Corpo & T;
  if (!r.ok) {
    const falhas = falhasDe(corpo);
    throw new ErroApi(r.status, corpo.code ?? 'erro',
      corpo.message ?? falhas[0]?.mensagem ?? 'Não foi possível concluir.', falhas);
  }
  return corpo as T;
}

/** Tool de leitura: GET /api/v1/<modulo>/<acao>?... */
export function ler<T>(tool: string, entrada: Record<string, unknown> = {}): Promise<T> {
  return enviar<T>(`${rotaDa(tool)}${querystring(entrada)}`, { method: 'GET' });
}

/** Tool de escrita: POST /api/v1/<modulo>/<acao> com JSON. */
export function escrever<T>(tool: string, entrada: unknown = {}): Promise<T> {
  return enviar<T>(rotaDa(tool), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entrada),
  });
}

/** Mensagem curta para a tela a partir de qualquer erro. */
export function mensagemDe(e: unknown): string {
  if (e instanceof ErroApi) return e.message;
  return 'Não foi possível falar com o servidor. Tente de novo.';
}
